import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { VariableEditorProps } from '../types';
import { VariableEditor } from '../VariableEditor';
import { EditorProvider } from '../EditorContext';
import { ItemActions } from './ItemActions';
import { CollapsibleItem } from './CollapsibleItem';
import { getDefaultForType, deepClone, getObjectSummary, generateUniqueKey } from './utils';

/**
 * Infer element type from actual values in the map
 */
function inferElementTypeFromValues(values: Record<string, unknown>): 'string' | 'number' | 'bool' | 'object' | 'list' | 'any' {
  const entries = Object.values(values);
  if (entries.length === 0) return 'any';

  const firstNonNull = entries.find(v => v !== null && v !== undefined);
  if (firstNonNull === undefined) return 'any';

  if (typeof firstNonNull === 'string') return 'string';
  if (typeof firstNonNull === 'number') return 'number';
  if (typeof firstNonNull === 'boolean') return 'bool';
  if (Array.isArray(firstNonNull)) return 'list';
  if (typeof firstNonNull === 'object') return 'object';

  return 'any';
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(2),
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1),
    },
    entryRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    keyInput: {
      flex: 1,
      maxWidth: 200,
    },
    valueInput: {
      flex: 2,
    },
    separator: {
      color: theme.palette.text.secondary,
      fontWeight: 500,
    },
    addButton: {
      marginTop: theme.spacing(1),
    },
    addRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
  }),
);

export const MapEditor: React.FC<VariableEditorProps> = ({
  variable,
  value,
  onChange,
  error,
  disabled,
}) => {
  const classes = useStyles();
  const [newKey, setNewKey] = useState('');
  // Track keys that were recently added (via add or duplicate) - these should be expanded with hidden visible
  const [newlyAddedKeys, setNewlyAddedKeys] = useState<Set<string>>(new Set());
  // Track keys that should start with edit mode active (for duplicated entries)
  const [editModeKeys, setEditModeKeys] = useState<Set<string>>(new Set());
  // Refs for scrolling to newly added entries
  const entryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const mapValue = (value as Record<string, unknown>) || {};
  const entries = Object.entries(mapValue);

  // Callback when an entry's expansion state changes - remove from newly added if collapsed
  const handleExpandChange = useCallback((key: string, expanded: boolean) => {
    if (!expanded && newlyAddedKeys.has(key)) {
      setNewlyAddedKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [newlyAddedKeys]);

  const declaredElementType = variable.elementType || 'string';

  // Infer actual element type from values if declared type is generic
  const inferredElementType = entries.length > 0
    ? inferElementTypeFromValues(mapValue)
    : declaredElementType;

  // Use explicit schema if available, otherwise determine from values
  const hasExplicitSchema = declaredElementType === 'object' && variable.elementSchema;

  // Check if values are actually complex (objects or arrays)
  const valuesAreComplex = inferredElementType === 'object' || inferredElementType === 'list';

  // Render as complex if we have explicit schema OR if actual values are complex
  const isComplexType = hasExplicitSchema || valuesAreComplex;

  const handleAddEntry = () => {
    if (newKey && !Object.prototype.hasOwnProperty.call(mapValue, newKey)) {
      let defaultValue: unknown;
      if (hasExplicitSchema) {
        // Use explicit schema for default value
        defaultValue = getDefaultForType('object', variable.elementSchema);
      } else if (valuesAreComplex) {
        // Infer default from existing values if they're complex
        const firstEntry = Object.values(mapValue)[0];
        if (firstEntry && typeof firstEntry === 'object' && !Array.isArray(firstEntry)) {
          // Create empty object with same keys as existing entries
          defaultValue = Object.keys(firstEntry as Record<string, unknown>).reduce((acc, k) => {
            acc[k] = null;
            return acc;
          }, {} as Record<string, unknown>);
        } else if (Array.isArray(firstEntry)) {
          defaultValue = [];
        } else {
          defaultValue = {};
        }
      } else {
        // Simple value type
        defaultValue = inferredElementType === 'number' ? 0 : '';
      }
      onChange({ ...mapValue, [newKey]: defaultValue });
      // Mark as newly added so it expands with hidden fields visible
      setNewlyAddedKeys(prev => new Set(prev).add(newKey));
      setNewKey('');
    }
  };

  const handleRemoveEntry = (key: string) => {
    const newMap = { ...mapValue };
    delete newMap[key];
    onChange(newMap);
    // Clean up from newly added tracking
    setNewlyAddedKeys(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const handleDuplicateEntry = (key: string) => {
    const existingKeys = Object.keys(mapValue);
    const newEntryKey = generateUniqueKey(existingKeys, `${key}_copy`);
    const clonedValue = deepClone(mapValue[key]);
    onChange({ ...mapValue, [newEntryKey]: clonedValue });
    // Mark as newly added so it expands with hidden fields visible
    setNewlyAddedKeys(prev => new Set(prev).add(newEntryKey));
    // Mark for immediate edit mode on the key
    setEditModeKeys(prev => new Set(prev).add(newEntryKey));
  };

  // Scroll to and focus newly duplicated entries
  useEffect(() => {
    if (editModeKeys.size > 0) {
      // Find the first key that needs scrolling
      const keyToScroll = Array.from(editModeKeys)[0];
      const element = entryRefs.current[keyToScroll];
      if (element) {
        // Scroll the element into view, centered
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [editModeKeys]);

  const handleKeyChange = (oldKey: string, newKeyValue: string) => {
    if (newKeyValue === oldKey) return;
    if (newKeyValue && !Object.prototype.hasOwnProperty.call(mapValue, newKeyValue)) {
      const newMap: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(mapValue)) {
        if (k === oldKey) {
          newMap[newKeyValue] = v;
        } else {
          newMap[k] = v;
        }
      }
      onChange(newMap);
    }
  };

  const handleValueChange = (key: string, newValue: unknown) => {
    onChange({ ...mapValue, [key]: newValue });
  };

  // Helper to get summary for any value type
  const getValueSummary = (val: unknown): string => {
    if (val === null) return 'null';
    if (val === undefined) return 'undefined';
    if (typeof val !== 'object') return String(val);
    if (Array.isArray(val)) return `[${val.length} items]`;
    return getObjectSummary(val as Record<string, unknown>);
  };

  // Render for complex values - use CollapsibleItem
  // This handles both explicit schema and inferred complex values
  if (isComplexType) {
    return (
      <Box className={classes.container}>
        <Box className={classes.header}>
          <Typography variant="body2">
            {variable.name}
            {variable.required && <span style={{ color: 'red' }}> *</span>}
            <Typography
              component="span"
              variant="caption"
              color="textSecondary"
              style={{ marginLeft: 8 }}
            >
              ({entries.length} {entries.length === 1 ? 'entry' : 'entries'})
            </Typography>
          </Typography>
        </Box>
        {variable.description && (
          <Typography variant="caption" color="textSecondary" paragraph>
            {variable.description}
          </Typography>
        )}

        {entries.map(([key, val], index) => {
          const isNewlyAdded = newlyAddedKeys.has(key);
          const shouldStartInEditMode = editModeKeys.has(key);

          // Calculate field count for complex values
          const fieldCount = val && typeof val === 'object' && !Array.isArray(val)
            ? Object.keys(val as Record<string, unknown>).length
            : Array.isArray(val)
              ? val.length
              : undefined;

          // Content for the collapsible item - directly show fields, no separate "Key" or "Value:" labels
          const entryContent = (
            <>
              {hasExplicitSchema && variable.elementSchema?.objectSchema ? (
                // Render with explicit schema
                Object.entries(variable.elementSchema.objectSchema).map(
                  ([fieldKey, fieldSchema]) => (
                    <Box key={fieldKey} mb={1}>
                      <VariableEditor
                        variable={{ ...fieldSchema, name: fieldKey }}
                        value={(val as Record<string, unknown>)?.[fieldKey]}
                        onChange={newFieldValue =>
                          handleValueChange(key, {
                            ...(val as Record<string, unknown>),
                            [fieldKey]: newFieldValue,
                          })
                        }
                        disabled={disabled}
                      />
                    </Box>
                  ),
                )
              ) : (
                // For inferred complex values, use VariableEditor with type inference
                <VariableEditor
                  variable={{
                    name: key,
                    type: Array.isArray(val) ? 'list' : 'object',
                    required: false,
                    sensitive: false,
                  }}
                  value={val}
                  onChange={newValue => handleValueChange(key, newValue)}
                  disabled={disabled}
                />
              )}
            </>
          );

          // Wrap newly added entries with EditorProvider to show hidden fields
          const wrappedContent = isNewlyAdded ? (
            <EditorProvider initialShowHidden>
              {entryContent}
            </EditorProvider>
          ) : (
            entryContent
          );

          return (
            <div
              key={key}
              ref={el => { entryRefs.current[key] = el; }}
            >
              <CollapsibleItem
                title={key}
                subtitle={getValueSummary(val)}
                fieldCount={fieldCount}
                defaultExpanded={false}
                forceExpanded={isNewlyAdded ? true : undefined}
                colorIndex={index}
                editable={!disabled}
                startInEditMode={shouldStartInEditMode}
                onTitleChange={newKey => handleKeyChange(key, newKey)}
                onEditModeEnd={() => {
                  setEditModeKeys(prev => {
                    const next = new Set(prev);
                    next.delete(key);
                    return next;
                  });
                }}
                onExpandChange={expanded => handleExpandChange(key, expanded)}
                disabled={disabled}
                actions={
                  <ItemActions
                    onDuplicate={() => handleDuplicateEntry(key)}
                    onDelete={() => handleRemoveEntry(key)}
                  disabled={disabled}
                />
              }
              >
                {wrappedContent}
              </CollapsibleItem>
            </div>
          );
        })}

        {!disabled && (
          <Box className={classes.addRow}>
            <TextField
              size="small"
              variant="outlined"
              value={newKey}
              onChange={e => setNewKey(e.target.value)}
              placeholder="New key"
              className={classes.keyInput}
              onKeyPress={e => {
                if (e.key === 'Enter') {
                  handleAddEntry();
                }
              }}
            />
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddEntry}
              disabled={!newKey || Object.prototype.hasOwnProperty.call(mapValue, newKey)}
              variant="outlined"
            >
              Add Entry
            </Button>
          </Box>
        )}

        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  // Render for simple value types (string, number, bool)
  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="body2">
          {variable.name}
          {variable.required && <span style={{ color: 'red' }}> *</span>}
          <Typography
            component="span"
            variant="caption"
            color="textSecondary"
            style={{ marginLeft: 8 }}
          >
            ({entries.length} {entries.length === 1 ? 'entry' : 'entries'})
          </Typography>
        </Typography>
      </Box>
      {variable.description && (
        <Typography variant="caption" color="textSecondary" paragraph>
          {variable.description}
        </Typography>
      )}

      {entries.map(([key, val]) => {
        // Determine value display: for primitive types show directly, for objects show JSON
        const isValComplex = val !== null && typeof val === 'object';
        const displayValue = isValComplex
          ? JSON.stringify(val, null, 2)
          : (val ?? '');
        const isNumberType = declaredElementType === 'number' || typeof val === 'number';

        return (
          <Box key={key} className={classes.entryRow}>
            <TextField
              size="small"
              variant="outlined"
              value={key}
              onChange={e => handleKeyChange(key, e.target.value)}
              disabled={disabled}
              placeholder="Key"
              className={classes.keyInput}
            />
            <Typography className={classes.separator}>=</Typography>
            {isValComplex ? (
              // For complex values that slipped through, use multiline JSON editor
              <TextField
                size="small"
                variant="outlined"
                multiline
                minRows={2}
                value={displayValue}
                onChange={e => {
                  try {
                    handleValueChange(key, JSON.parse(e.target.value));
                  } catch {
                    // Keep current value if not valid JSON
                  }
                }}
                disabled={disabled}
                placeholder="Value (JSON)"
                className={classes.valueInput}
              />
            ) : (
              <TextField
                size="small"
                variant="outlined"
                type={isNumberType ? 'number' : 'text'}
                value={displayValue}
                onChange={e =>
                  handleValueChange(
                    key,
                    isNumberType
                      ? Number(e.target.value)
                      : e.target.value,
                  )
                }
                disabled={disabled}
                placeholder="Value"
                className={classes.valueInput}
              />
            )}
            <ItemActions
              onDuplicate={() => handleDuplicateEntry(key)}
              onDelete={() => handleRemoveEntry(key)}
              disabled={disabled}
            />
          </Box>
        );
      })}

      {!disabled && (
        <Box className={classes.addRow}>
          <TextField
            size="small"
            variant="outlined"
            value={newKey}
            onChange={e => setNewKey(e.target.value)}
            placeholder="New key"
            className={classes.keyInput}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                handleAddEntry();
              }
            }}
          />
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddEntry}
            disabled={!newKey || Object.prototype.hasOwnProperty.call(mapValue, newKey)}
            variant="outlined"
          >
            Add
          </Button>
        </Box>
      )}

      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Box>
  );
};
